/**
 * Whether this page is trapped inside somebody else's app, and how to get out.
 *
 * A customer who taps a link in a Messenger thread is not in a browser they chose. They are
 * in Facebook's, and **Google refuses OAuth inside an embedded webview** — so signing in
 * cannot be made to work there by anything on our side. `services/auth.js` has said so for
 * a while, but only after the attempt has already failed, which is one dead end too late.
 *
 * This is the same knowledge, offered before the wall rather than at it.
 *
 * Pure and argument-taking so it can be tested without a browser, which is the only way
 * anything in this app gets tested at all.
 */

/**
 * Deliberately narrow.
 *
 * Matching loosely on `wv` or `Mobile` sweeps in ordinary Chrome for Android, and telling
 * somebody to leave the browser they are already in is worse than saying nothing. The list
 * is the apps whose webviews people actually arrive from — the same one `auth.js` used, now
 * with one owner.
 *
 * `FBAN` and `FBAV` are Facebook's own tokens; `FB_IAB` is its in-app-browser marker.
 */
const IN_APP = /FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok|Twitter|Snapchat/i

export const isInAppBrowser = (ua = globalThis.navigator?.userAgent ?? '') => IN_APP.test(String(ua))

export const platformOf = (ua = globalThis.navigator?.userAgent ?? '') => {
  const text = String(ua)

  if (/Android/i.test(text)) return 'android'
  if (/iPhone|iPad|iPod/i.test(text)) return 'ios'

  return 'other'
}

/**
 * A link that leaves the webview, or null where the platform has no such thing.
 *
 * **Android** has a real answer. An `intent://` URL naming Chrome is handled by the system
 * rather than by the app showing the page, so the webview cannot keep it. `S.browser_fallback_url`
 * is what runs when Chrome is not installed — without it the tap does nothing at all, which
 * is the worst outcome of the three.
 *
 * **iOS has no reliable one.** `x-safari-https:` was honoured by some webviews and is not a
 * documented escape; Apple never shipped a supported way for a page to eject itself. So the
 * screen asks the person instead, and the copy names the menu they are looking at. Guessing
 * at a scheme that silently fails would be worse than telling them the truth.
 *
 * @returns {string|null}
 */
export function externalUrlFor(href, ua = globalThis.navigator?.userAgent ?? '') {
  const url = String(href ?? '')

  if (!/^https?:\/\//i.test(url)) return null

  if (platformOf(ua) !== 'android') return null

  const withoutScheme = url.replace(/^https?:\/\//i, '')

  return `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;`
    + `S.browser_fallback_url=${encodeURIComponent(url)};end`
}

/**
 * What the person has to press, in their own words, when the page cannot do it for them.
 *
 * Named per app because the menus differ and "open this in your browser" is not an
 * instruction anybody can follow while looking at three dots in a corner.
 */
export function escapeHint(ua = globalThis.navigator?.userAgent ?? '') {
  if (platformOf(ua) === 'ios') {
    return 'Pindutin ang ••• sa kanang itaas, tapos piliin ang Open in Safari.'
  }

  return 'Pindutin ang ⋮ sa kanang itaas, tapos piliin ang Open in browser.'
}
